import { calcularForcaDeChoque } from '../dominio/tabletop/forcaDeChoque'
import { criarDetectorDeImpacto } from '../dominio/tabletop/impactoQueda'
import { pontoNoMundo } from '../dominio/tabletop/ancoragem'
import { parametrosDoObjeto } from '../dominio/tabletop/parametros'
import {
  prepararQueda,
  quedaLivreAteEsticar,
  type FaseQueda,
  type ImpactoQueda,
  type QuadroQueda,
  type ResultadoQuedaAnimada,
  type RompimentoQueda,
} from '../dominio/tabletop/quedaAnimada'
import { entradaDeChoque } from '../dominio/tabletop/quedaEmAltura'
import type { CenaTabletop, VetorTabletop } from '../dominio/tabletop/tipos'
import type { Rapier } from './motor'
import { simularQueda } from './simulacaoQueda'

/**
 * A queda de um trabalhador da cena, simulada com o motor de física, no mesmo
 * formato da queda calculada (`simularQuedaAnimada`): a cena, a ponte e a
 * régua de tempo usam os dois sem saber qual é qual. O cálculo da força de
 * choque vem junto em `choque`, para comparar com o pico simulado.
 *
 * A diferença que importa: aqui os montantes da torre bloqueiam o balanço, e o
 * corpo balança em arco enquanto freia, em vez de descer na vertical.
 */

const FASES: Readonly<Record<string, FaseQueda>> = {
  'queda-livre': 'queda-livre',
  frenagem: 'frenagem',
  pendulo: 'pendulo',
  parado: 'parado',
  rompido: 'rompido',
}

export function simularQuedaComFisica(R: Rapier, cena: CenaTabletop, idCorda: string): ResultadoQuedaAnimada | null {
  const preparo = prepararQueda(cena, idCorda, 'fisica')
  if (!preparo) return null
  if (preparo.semQueda) return preparo.semQueda
  const { trabalhador, comprimentoM: L, corpo, ancoragem: A, base } = preparo
  const saida = preparo.saida!

  const p = trabalhador.parametros
  const fitaDanificada = p?.fitaDanificada === 1
  const cargaRupturaN = fitaDanificada ? 4000 : p?.cargaRupturaKN ? p.cargaRupturaKN * 1000 : 22000

  const geometria = quedaLivreAteEsticar(saida, A, L)
  const entrada = entradaDeChoque(trabalhador, geometria.hLivre)
  const choque = calcularForcaDeChoque(entrada)
  const detectar = criarDetectorDeImpacto(cena, { montantes: true })
  const sim = simularQueda(
    R,
    {
      ancoragem: A,
      partida: saida,
      massaKg: entrada.massaKg,
      comprimentoM: L,
      rigidezEAkN: entrada.rigidezEAkN,
      forcaRedutorN: entrada.forcaAbsorvedorKN * 1000,
      aberturaMaximaM: entrada.aberturaMaximaAbsorvedorM,
      cargaRupturaN,
    },
    (anterior, atual) => detectar(anterior, atual, corpo),
  )

  const quadros: QuadroQueda[] = sim.quadros.map((q) => ({
    t: q.t,
    fase: FASES[q.fase] ?? 'queda-livre',
    engate: q.engate,
    pes: [q.engate[0], q.engate[1] - corpo.engateAosPesM, q.engate[2]],
    forcaKN: q.forcaN / 1000,
  }))
  const impacto: ImpactoQueda | null = sim.impacto
    ? {
        quadro: sim.impacto.quadro,
        t: sim.impacto.t,
        ponto: sim.impacto.ponto,
        parteDoCorpo: sim.impacto.parte,
        alvo: sim.impacto.alvo,
        velocidadeMs: sim.impacto.velocidadeMs,
        fase: FASES[sim.impacto.fase] ?? 'queda-livre',
      }
    : null

  const rompimento: RompimentoQueda | null = sim.rompimento
    ? {
        t: sim.rompimento.t,
        forcaKN: sim.rompimento.forcaN / 1000,
        quadro: sim.rompimento.quadro,
        alturaM: sim.rompimento.alturaM,
        cargaRupturaKN: sim.rompimento.cargaRupturaN !== undefined ? sim.rompimento.cargaRupturaN / 1000 : undefined,
        velocidadeImpactoMs: sim.rompimento.velocidadeImpactoMs,
      }
    : null

  const tenso: VetorTabletop = sim.engateAoEsticar ?? saida
  const parada: VetorTabletop = sim.engateNoFimDaFrenagem ?? tenso
  const raioNaParada = Math.hypot(parada[0] - A[0], parada[1] - A[1], parada[2] - A[2])
  const desvioNaParada = Math.hypot(parada[0] - A[0], parada[2] - A[2])
  const ultimo = quadros[quadros.length - 1]!
  return {
    ...base,
    situacao: 'ok',
    quadros,
    pontoDeSaida: saida,
    pontoTenso: tenso,
    pontoDeParada: parada,
    quedaLivreM: saida[1] - tenso[1],
    velocidadeAoEsticarMs: sim.velocidadeAoEsticarMs,
    anguloDoPenduloGraus: raioNaParada > 1e-9 ? (Math.asin(Math.min(1, desvioNaParada / raioNaParada)) * 180) / Math.PI : 0,
    impacto,
    rompimento,
    pesFinaisY: impacto ? null : ultimo.pes[1],
    duracaoS: ultimo.t,
    choque,
    curvaDeForca: sim.curva,
    inicioDaFrenagemS: sim.tEsticouS ?? 0,
    simulado: {
      picoKN: sim.picoForcaN / 1000,
      aberturaUsadaM: sim.aberturaUsadaM,
      descidaAposEsticarM: sim.descidaAposEsticarM,
      alongamentoNoPicoM: sim.alongamentoNoPicoM,
    },
  }
}

/**
 * Simula a queda de um trabalhador solto no ar sem talabarte ou amarra, até colidir
 * com o chão ou patamar da torre.
 */
export function simularQuedaLivreDoTrabalhador(R: Rapier, cena: CenaTabletop, idTrabalhador: string): ResultadoQuedaAnimada | null {
  const trabalhador = cena.objetos.find((o) => o.id === idTrabalhador)
  if (!trabalhador || trabalhador.tipo !== 'trabalhador') return null
  const p = parametrosDoObjeto(trabalhador)
  const engateAosPesM = p.engateAosPesM ?? 1.45
  const corpo = { engateAosPesM }
  const engate0 = pontoNoMundo(trabalhador, 'engate-dorsal')
  const guinada = trabalhador.transformacao.rotacao[1]
  const massaKg = p.massaKg ?? 100

  const detectar = criarDetectorDeImpacto(cena, { montantes: true })
  const sim = simularQueda(R, { partida: engate0, massaKg }, (anterior, atual) => detectar(anterior, atual, corpo))

  const quadros: QuadroQueda[] = sim.quadros.map((q) => ({
    t: q.t,
    fase: FASES[q.fase] ?? 'queda-livre',
    engate: q.engate,
    pes: [q.engate[0], q.engate[1] - corpo.engateAosPesM, q.engate[2]],
    forcaKN: 0,
  }))

  const impacto: ImpactoQueda | null = sim.impacto
    ? {
        quadro: sim.impacto.quadro,
        t: sim.impacto.t,
        ponto: sim.impacto.ponto,
        parteDoCorpo: sim.impacto.parte,
        alvo: sim.impacto.alvo,
        velocidadeMs: sim.impacto.velocidadeMs,
        fase: FASES[sim.impacto.fase] ?? 'queda-livre',
      }
    : null

  const ultimo = quadros[quadros.length - 1] ?? { t: 0, pes: [engate0[0], engate0[1] - engateAosPesM, engate0[2]] }
  return {
    idCorda: '',
    idTrabalhador,
    pontoAncoragem: engate0,
    guinadaRad: guinada,
    anguloDoPenduloGraus: 0,
    impacto,
    pesFinaisY: impacto ? null : ultimo.pes[1],
    duracaoS: ultimo.t,
    choque: null,
    curvaDeForca: null,
    inicioDaFrenagemS: 0,
    origem: 'fisica',
    simulado: null,
    situacao: 'ok',
    quadros,
    pontoDeSaida: engate0,
    pontoTenso: engate0,
    pontoDeParada: impacto ? impacto.ponto : [ultimo.pes[0], ultimo.pes[1] + engateAosPesM, ultimo.pes[2]],
    quedaLivreM: engate0[1] - (impacto ? impacto.ponto[1] : ultimo.pes[1]),
    velocidadeAoEsticarMs: 0,
    rompimento: null,
  }
}
